import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageBanner } from "@/components/ui/PageBanner";
import { IMG } from "@/lib/assets";
import { today, prettyDay } from "@/lib/perform";
import { Check, Flame, Plus, Trash2, Users, Sparkles } from "lucide-react";

const field = "h-10 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm w-full";

function NewPlan({ staff, onDone }) {
  const { data: templates = [] } = useQuery({ queryKey: ["habit-templates"], queryFn: async () => (await api.get("/api/habits/templates")).data });
  const [f, setF] = useState(null);
  const start = (t) => setF({ title: t?.title || "", description: t?.description || "", days: t?.days || 28, start: today(),
    habits: (t?.habits || [{ name: "", target: "" }]).map((h) => ({ ...h })), scope: "personal" });
  const save = async () => {
    try { await api.post("/api/habits/plans", { ...f, days: Number(f.days), habits: f.habits.filter((h) => h.name.trim()) }); toast.success("Plan started"); setF(null); onDone(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  if (!f) {
    return (
      <div className="surface rounded-2xl p-5">
        <p className="font-heading font-bold flex items-center gap-2"><Sparkles className="w-4 h-4 text-acid" /> Start a plan</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
          {templates.map((t) => (
            <button key={t.title} onClick={() => start(t)} className="text-left rounded-xl border border-[#1E222A] bg-[#0B0D11] p-4 hover:border-acid/60">
              <p className="font-semibold">{t.title}</p><p className="text-xs text-[#8A91A6] mt-1">{t.days} days · {t.habits.length} habits</p>
            </button>
          ))}
          <button onClick={() => start(null)} className="rounded-xl border border-dashed border-[#343A4A] p-4 text-sm text-acid inline-flex items-center justify-center gap-1" data-testid="habit-custom"><Plus className="w-4 h-4" /> Build your own</button>
        </div>
      </div>
    );
  }
  return (
    <div className="surface rounded-2xl p-5 space-y-2" data-testid="habit-form">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <input className={`${field} md:col-span-2`} placeholder="Plan name" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <input className={field} type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} />
        <input className={field} type="number" min="3" max="120" value={f.days} onChange={(e) => setF({ ...f, days: e.target.value })} />
      </div>
      {f.habits.map((h, i) => (
        <div key={i} className="flex gap-2">
          <input className={field} placeholder="Habit, e.g. Sleep 7+ hours" value={h.name} onChange={(e) => setF({ ...f, habits: f.habits.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
          <button onClick={() => setF({ ...f, habits: f.habits.filter((_, j) => j !== i) })} className="p-2 text-[#8A91A6]" aria-label="Remove habit"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button onClick={() => setF({ ...f, habits: [...f.habits, { name: "", target: "" }] })} className="text-sm text-acid inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Add habit</button>
        <div className="flex items-center gap-3">
          {staff && <label className="text-sm text-[#B7BECB] flex items-center gap-2"><input type="checkbox" checked={f.scope === "club"} onChange={(e) => setF({ ...f, scope: e.target.checked ? "club" : "personal" })} /> Assign to my whole club</label>}
          <button onClick={() => setF(null)} className="h-10 px-4 rounded-full border border-[#343A4A] text-sm">Cancel</button>
          <button onClick={save} className="h-10 px-5 rounded-full bg-acid text-black font-medium" data-testid="habit-save">Start</button>
        </div>
      </div>
    </div>
  );
}

function ClubProgress({ id }) {
  const { data } = useQuery({ queryKey: ["habit-club", id], queryFn: async () => (await api.get(`/api/habits/plans/${id}/club`)).data });
  if (!data) return null;
  return (
    <div className="mt-3 space-y-1">
      <p className="mono-label">squad completion</p>
      {data.athletes.map((a, i) => (
        <div key={i} className="flex items-center gap-2 text-sm">
          <span className="w-32 truncate">{a.name}</span>
          <div className="flex-1 h-1.5 rounded-full bg-[#1E222A] overflow-hidden"><div className="h-full bg-acid" style={{ width: `${a.completion}%` }} /></div>
          <span className="w-10 text-right font-mono2 text-xs">{a.completion}%</span>
        </div>
      ))}
    </div>
  );
}

export default function Habits() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const staff = ["owner", "coach"].includes(user?.club_role) && user?.club_active;
  const { data = [] } = useQuery({ queryKey: ["habit-plans"], queryFn: async () => (await api.get("/api/habits/plans", { params: { tz_offset: new Date().getTimezoneOffset() } })).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["habit-plans"] });
  const tick = async (p, date, habit, done) => {
    try { await api.post(`/api/habits/plans/${p.id}/check`, { date, habit, done, tz_offset: new Date().getTimezoneOffset() }); refresh(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.nutrition} overline="lifestyle" title="Habits that hold"
        subtitle="Sleep, food, water, steps. Small daily wins your training is built on. Tick them off and keep the streak alive." />
      <NewPlan staff={staff} onDone={refresh} />
      {data.length === 0 && <p className="text-sm text-[#8A91A6] text-center">No plans yet. Pick one above.</p>}
      {data.map((p) => (
        <motion.div key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="surface rounded-2xl p-5" data-testid="habit-plan">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="mono-label flex items-center gap-1.5">{p.scope === "club" && <Users className="w-3.5 h-3.5" />}{p.status} · day {p.day} of {p.days}</p>
              <p className="font-heading font-black text-xl">{p.title}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center"><p className="font-heading font-black text-2xl flex items-center gap-1"><Flame className="w-5 h-5 text-[#FFB340]" />{p.streak}</p><p className="mono-label">streak</p></div>
              <div className="text-center"><p className="font-heading font-black text-2xl text-acid">{p.completion}%</p><p className="mono-label">done</p></div>
              {p.can_delete && <button onClick={async () => { if (window.confirm("Delete this plan?")) { await api.delete(`/api/habits/plans/${p.id}`); refresh(); } }} className="text-[#8A91A6] hover:text-pulseError" aria-label="Delete plan"><Trash2 className="w-4 h-4" /></button>}
            </div>
          </div>
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm">
              <thead><tr><th className="text-left py-1"></th>{p.week.map((d) => <th key={d.date} className="mono-label px-1 text-center">{prettyDay(d.date, { weekday: "short" })}<br />{Number(d.date.slice(8))}</th>)}</tr></thead>
              <tbody>
                {p.habits.map((h, i) => (
                  <tr key={i}>
                    <td className="py-1.5 pr-3">{h.name}</td>
                    {p.week.map((d) => {
                      const on = d.done[i]; const future = d.date > today();
                      const inPlan = d.date >= p.start && d.date <= p.end;
                      return (
                        <td key={d.date} className="px-1 text-center">
                          <button disabled={future || !inPlan} onClick={() => tick(p, d.date, i, !on)} data-testid="habit-tick"
                            className={`w-8 h-8 rounded-lg border inline-flex items-center justify-center transition-colors ${on ? "bg-acid border-acid text-black" : "border-[#343A4A] hover:border-acid/60"} disabled:opacity-25`}>
                            {on && <Check className="w-4 h-4" />}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {p.scope === "club" && staff && <ClubProgress id={p.id} />}
        </motion.div>
      ))}
    </div>
  );
}
